/**
 * Audit Trail Visualizer — Iter-99
 *
 * The external-audit layer. Pick any source document and see the
 * full chain of:
 *   Source → Audit Events → Journals → VAT Return → Period Lock
 * plus side-effects (auto-generated contract amendments for VOs).
 *
 * Built for Big4 auditors, CFOs, and external compliance reviewers.
 */
import React, { useEffect, useMemo, useState } from "react";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import {
  Search, FileText, Receipt, GitBranch, ScrollText, ShieldCheck,
  Loader2, ArrowRight, Lock, CheckCircle2, AlertTriangle,
  ClipboardCopy, Activity, Coins, Building2, Banknote,
  FileDown, Package,
} from "lucide-react";

const SAR = (n) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "SAR", maximumFractionDigits: 0,
}).format(Number(n || 0));

const ENTITY_META = {
  progress_claim:     { icon: Receipt,    color: "indigo",  label: "Progress Claim" },
  invoice:            { icon: FileText,   color: "blue",    label: "Invoice" },
  variation_order:    { icon: GitBranch,  color: "violet",  label: "Variation Order" },
  site_instruction:   { icon: ScrollText, color: "sky",     label: "Site Instruction" },
  contract_amendment: { icon: ShieldCheck, color: "emerald", label: "Amendment" },
  retention_release:  { icon: Coins,      color: "amber",   label: "Retention Release" },
  journal:            { icon: Banknote,   color: "slate",   label: "Journal" },
  vat_return:         { icon: Building2,  color: "rose",    label: "VAT Return" },
};

const VERDICT_BADGE = {
  verified:   { cls: "bg-emerald-100 text-emerald-800 border-emerald-300",
                icon: CheckCircle2, label: "Verified · سلسلة مدققة" },
  locked:     { cls: "bg-rose-100 text-rose-800 border-rose-300",
                icon: Lock,         label: "Locked · فترة مغلقة" },
  incomplete: { cls: "bg-amber-100 text-amber-800 border-amber-300",
                icon: AlertTriangle, label: "Incomplete · سلسلة غير مكتملة" },
};

const STEP_COLORS = {
  source:     "border-indigo-300 bg-indigo-50",
  event:      "border-sky-300 bg-sky-50",
  journal:    "border-slate-300 bg-slate-50",
  vat_return: "border-rose-300 bg-rose-50",
  amendment:  "border-emerald-300 bg-emerald-50",
};

// ─── Summary tiles ────────────────────────────────────────────────

const SummaryTile = ({ label, value, icon: Icon, color = "slate" }) => (
  <div data-testid={`summary-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}
       className={`flex items-center gap-3 rounded-xl border border-${color}-200 bg-white p-4 shadow-sm`}>
    <div className={`flex h-10 w-10 items-center justify-center rounded-lg bg-${color}-100 text-${color}-700`}>
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-lg font-bold text-slate-900">{value}</div>
    </div>
  </div>
);

// ─── Recent / Search panel ────────────────────────────────────────

const SearchPanel = ({ onPick }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axiosInstance.get("/audit-trail/recent?limit=12");
        setRecent(r.data?.items || []);
      } catch { /* silent — non-blocking */ }
    })();
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axiosInstance.get(`/audit-trail/search`,
                                                { params: { q: q.trim() } });
        setResults(r.data?.matches || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const items = q.trim().length >= 2 ? results : recent;
  const heading = q.trim().length >= 2
    ? `${results.length} نتيجة لكلمة "${q}"`
    : "أحدث المستندات القابلة للتتبع";

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="audit-trail-search"
          dir="ltr"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ابحث: INV-00001 · VO-00010 · PC-00003 · JRN-01234 · VAT-2026Q2"
          className="w-full rounded-xl border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm font-mono placeholder:font-sans placeholder:text-slate-400 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" />}
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">{heading}</h3>
        </div>
        <ul data-testid="audit-trail-pick-list" className="grid gap-2">
          {items.length === 0 && (
            <li className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-400">
              {q.trim().length >= 2 ? "لا توجد نتائج." : "لا توجد مستندات حتى الآن."}
            </li>
          )}
          {items.map((it) => {
            const meta = ENTITY_META[it.entity_type] || ENTITY_META.invoice;
            const Icon = meta.icon;
            return (
              <li key={`${it.entity_type}:${it.entity_id}`}>
                <button
                  type="button"
                  data-testid={`pick-${it.entity_type}-${it.entity_id}`}
                  onClick={() => onPick(it)}
                  className="flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 text-left transition-all hover:border-violet-400 hover:bg-violet-50"
                >
                  <div className={`flex h-9 w-9 items-center justify-center rounded-lg bg-${meta.color}-100 text-${meta.color}-700`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-bold text-slate-900">{it.label}</span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600">{meta.label}</span>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-slate-500">
                      {it.title || it.project_name || "—"}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-300" />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

// ─── Trace timeline ───────────────────────────────────────────────

const ChainStep = ({ step }) => {
  const meta = ENTITY_META[step.kind] || ENTITY_META[step.type] || { icon: Activity, color: "slate", label: step.type };
  const Icon = meta.icon;
  const tone = STEP_COLORS[step.type] || "border-slate-300 bg-slate-50";
  return (
    <li data-testid={`chain-step-${step.step}`} className="relative pl-10">
      <div className={`absolute left-0 top-0 flex h-8 w-8 items-center justify-center rounded-full border-2 ${tone}`}>
        <Icon className="h-4 w-4 text-slate-700" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tone}`}>
              {step.type}
            </span>
            <span className="font-mono text-sm font-bold text-slate-900">{step.label}</span>
            {step.status && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-700">
                {step.status}
              </span>
            )}
          </div>
          <span dir="ltr" className="text-[11px] font-mono text-slate-500">
            {String(step.ts || "").slice(0, 19).replace("T", " ") || "—"}
          </span>
        </div>
        {(step.debit || step.credit) && (
          <div className="mt-2 flex items-center gap-4 text-xs">
            <span className="text-emerald-700">Dr: {SAR(step.debit)}</span>
            <span className="text-rose-700">Cr: {SAR(step.credit)}</span>
          </div>
        )}
        {step.actor && (
          <div className="mt-1 text-xs text-slate-500">بواسطة: {step.actor}</div>
        )}
      </div>
    </li>
  );
};

const TraceView = ({ trace, onClear }) => {
  const v = VERDICT_BADGE[trace.verdict] || VERDICT_BADGE.incomplete;
  const VIcon = v.icon;
  const [downloading, setDownloading] = useState(null); // 'pdf' | 'zip' | null

  const copySummary = () => {
    const s = trace.summary;
    const text = `AUDIT TRAIL — ${trace.source.label}
Verdict: ${trace.verdict.toUpperCase()}
Events:           ${s.events}
Journals:         ${s.journals}  (Dr ${s.journal_total_dr}, Cr ${s.journal_total_cr})
VAT Returns:      ${s.vat_returns}  (locked: ${s.locked_periods})
Amendments:       ${s.amendments}

Chain:
${trace.chain.map(c => `  ${c.step}. [${c.type}] ${c.label}  ${c.ts || ""}`).join("\n")}`;
    navigator.clipboard.writeText(text);
    toast.success("تم النسخ للحافظة — جاهز لإرفاقه في تقرير التدقيق");
  };

  const downloadPackage = async (kind) => {
    setDownloading(kind);
    try {
      const url = `/audit-trail/package/${kind}?entity_type=${encodeURIComponent(trace.source.entity_type)}&entity_id=${encodeURIComponent(trace.source.entity_id)}`;
      const res = await axiosInstance.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: kind === "pdf" ? "application/pdf" : "application/zip",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = kind === "pdf"
        ? `audit_package_${trace.source.label}.pdf`
        : `audit_bundle_${trace.source.label}.zip`;
      document.body.appendChild(link); link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(link.href), 2000);
      toast.success(kind === "pdf"
        ? "تم تحميل تقرير التدقيق (PDF)"
        : "تم تحميل حزمة التدقيق الكاملة (ZIP)");
    } catch (e) {
      toast.error("فشل تنزيل حزمة التدقيق");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div data-testid="audit-trail-trace" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-slate-200 bg-gradient-to-r from-white to-slate-50 p-4 shadow-sm">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">المستند الأساسي</div>
          <div className="mt-1 flex items-center gap-2">
            <span className="font-mono text-2xl font-bold text-slate-900">{trace.source.label}</span>
            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-700">
              {ENTITY_META[trace.source.entity_type]?.label || trace.source.entity_type}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span data-testid="audit-trail-verdict"
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-bold ${v.cls}`}>
            <VIcon className="h-4 w-4" />
            {v.label}
          </span>
          <button
            type="button"
            data-testid="audit-trail-copy"
            onClick={copySummary}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-violet-400 hover:bg-violet-50"
          >
            <ClipboardCopy className="h-3.5 w-3.5" />نسخ كتقرير
          </button>
          <button
            type="button"
            data-testid="audit-trail-download-pdf"
            disabled={downloading === "pdf"}
            onClick={() => downloadPackage("pdf")}
            className="inline-flex items-center gap-1.5 rounded-lg border border-violet-500 bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-violet-700 disabled:opacity-60"
          >
            {downloading === "pdf"
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <FileDown className="h-3.5 w-3.5" />}
            تنزيل PDF
          </button>
          <button
            type="button"
            data-testid="audit-trail-download-zip"
            disabled={downloading === "zip"}
            onClick={() => downloadPackage("zip")}
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
          >
            {downloading === "zip"
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Package className="h-3.5 w-3.5" />}
            تنزيل Bundle
          </button>
          <button
            type="button"
            data-testid="audit-trail-clear"
            onClick={onClear}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-slate-400"
          >
            رجوع
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <SummaryTile label="Events" value={trace.summary.events} icon={Activity} color="sky" />
        <SummaryTile label="Journals" value={trace.summary.journals} icon={Banknote} color="slate" />
        <SummaryTile label="Dr Total" value={SAR(trace.summary.journal_total_dr)} icon={Coins} color="emerald" />
        <SummaryTile label="VAT Returns" value={`${trace.summary.vat_returns} (${trace.summary.locked_periods} مغلقة)`} icon={Building2} color="rose" />
        <SummaryTile label="Amendments" value={trace.summary.amendments} icon={ShieldCheck} color="violet" />
      </div>

      {/* Chain timeline */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="mb-4 text-sm font-bold text-slate-900">
          سلسلة التدقيق الزمنية ({trace.chain.length} خطوة)
        </h3>
        <ol data-testid="audit-trail-chain" className="relative space-y-3 border-l-2 border-dashed border-slate-200 pl-2">
          {trace.chain.map((step) => (
            <ChainStep key={step.step} step={step} />
          ))}
        </ol>
      </div>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────

export default function AuditTrailVisualizer() {
  const [picked, setPicked] = useState(null);
  const [trace, setTrace] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axiosInstance.get("/audit-trail/summary");
        setSummary(r.data || null);
      } catch { /* silent */ }
    })();
  }, []);

  const onPick = async (item) => {
    setPicked(item);
    setTrace(null);
    setLoading(true);
    try {
      const r = await axiosInstance.get("/audit-trail/trace", {
        params: { entity_type: item.entity_type, entity_id: item.entity_id },
      });
      setTrace(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل جلب سلسلة التدقيق");
      setPicked(null);
    } finally {
      setLoading(false);
    }
  };

  const onClear = () => { setPicked(null); setTrace(null); };

  return (
    <div data-testid="audit-trail-visualizer" className="space-y-6 p-6" dir="rtl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">
          <ShieldCheck className="inline -mt-1 ml-2 h-7 w-7 text-violet-600" />
          Audit Trail Visualizer
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          طبقة التدقيق الخارجي — تتبع كل عملية مالية من مصدرها حتى ترحيلها والإغلاق
          <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700">
            Source → Events → Journals → VAT → Lock
          </span>
        </p>
      </div>

      {/* Portfolio summary tiles */}
      {summary && (
        <div data-testid="audit-trail-summary" className="grid grid-cols-2 gap-3 md:grid-cols-5" dir="ltr">
          <SummaryTile label="Progress Claims" value={summary.progress_claims} icon={Receipt} color="indigo" />
          <SummaryTile label="Invoices" value={summary.invoices} icon={FileText} color="blue" />
          <SummaryTile label="VOs" value={summary.variation_orders} icon={GitBranch} color="violet" />
          <SummaryTile label="Journals" value={summary.journals} icon={Banknote} color="slate" />
          <SummaryTile label="VAT (locked)" value={`${summary.vat_returns} (${summary.vat_locked})`} icon={Lock} color="rose" />
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3" dir="rtl">
        {/* Left rail — search & pick */}
        <div className={picked ? "lg:col-span-1" : "lg:col-span-3"}>
          <SearchPanel onPick={onPick} />
        </div>

        {/* Right pane — trace */}
        {picked && (
          <div className="lg:col-span-2">
            {loading && (
              <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 text-slate-500">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" /> جارٍ بناء سلسلة التدقيق…
              </div>
            )}
            {!loading && trace && (
              <TraceView trace={trace} onClear={onClear} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
